import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import typography from "../../cssUtils/typography";

// WhatsApp daily messaging limit (Meta tier) for the broadcast being set up.
// `limit` is the response of POST /campaign/messaging-limit/ (campaign/messaging_limit.py capacity()).

const COLORS = {
  ok: { bg: "#E8F5EE", border: "#A9D8BD", fg: "#0B6B3A", bar: "#1A7F4B" },
  warn: { bg: "#FFF4E0", border: "#F0C27A", fg: "#7A4500", bar: "#C77700" },
  error: { bg: "#FDECEA", border: "#F3B4AD", fg: "#B42318", bar: "#D92D20" },
};
const INFO = { bg: "#EEF3FF", border: "#B9C8F0", fg: "#1E3A8A" };
const BLOCKING = ["over", "over_tier"];

const fmt = (n) => (n ?? 0).toLocaleString("en-US");
const minutesAgo = (iso) => {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return Number.isNaN(minutes) || minutes < 1 ? "just now" : minutes < 60 ? `${minutes} min ago` : `${Math.round(minutes / 60)}h ago`;
};
const tierShort = (limit) =>
  limit.unlimited ? "Unlimited" : limit.limit >= 1000 ? `${limit.limit / 1000}K / day` : `${fmt(limit.limit)} / day`;

// Everything the card, the audience note and the footer show, from one response
export const describeLimit = (limit, isLater) => {
  if (!limit || !limit.configured) return null;
  const hasCap = limit.limit != null;
  const remaining = limit.remaining ?? 0;
  const adds = limit.adds ?? 0;
  const blocked = !isLater && BLOCKING.includes(limit.status);
  const tone = { fits: "ok", no_limit: "ok", close: "warn", unknown: "warn" }[limit.status] || (blocked ? "error" : "warn");

  let title = "";
  let body = "";
  const share = remaining ? Math.round((adds / remaining) * 100) : 100;
  if (limit.status === "close") {
    title = isLater ? "Close to your limit at send time" : "Close to your daily limit";
    body = `This broadcast uses ${share}% of the ${isLater ? "projected" : "remaining"} capacity. Only ${fmt(remaining - adds)} contacts will be left for other broadcasts.`;
  } else if (limit.status === "over_tier") {
    title = isLater ? "Likely to exceed your limit at send time" : "Audience exceeds your remaining daily limit";
    body = `Audience ${fmt(limit.audience)} is larger than your whole tier (${tierShort(limit)}). Split it into smaller campaigns${isLater ? "" : " across days"}.`;
  } else if (limit.status === "over") {
    title = isLater ? "Likely to exceed your limit at send time" : "Audience exceeds your remaining daily limit";
    body = isLater
      ? `Projected ${fmt(remaining)} contacts left at send time. We'll re-check before sending and hold the campaign if it doesn't fit.`
      : `This broadcast adds ${fmt(adds)} contacts, more than the ${fmt(remaining)} left on tier ${tierShort(limit)}. Pick a smaller audience, or schedule it for later when capacity frees up.`;
  } else if (limit.status === "unknown") {
    title = "Couldn't read your messaging tier";
    body = `Gupshup returned tier "${limit.tier}". No limit is checked for this broadcast.`;
  }

  return {
    ...limit,
    hasCap,
    tone,
    blocked,
    title,
    body,
    blockedMessage: !blocked
      ? null
      : limit.status === "over_tier"
        ? "This audience is larger than your whole daily tier. Split it across days to continue."
        : `Reduce the audience to ${fmt(limit.reduce_to)} or fewer to continue.`,
    usedTotal: hasCap ? limit.limit - remaining : limit.used,
  };
};

const Tile = ({ label, value, sub, tone }) => {
  const color = tone ? COLORS[tone] : null;
  return (
    <Box sx={{ flex: "1 1 160px", border: `1px solid ${color ? color.border : "#E3E5EA"}`, bgcolor: color ? color.bg : "#fff", borderRadius: "8px", px: 2, py: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{ ...typography.bodySmall, fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: color ? color.fg : "#5B6075" }}>{label}</Typography>
      {typeof value === "string" ? <Typography sx={{ fontSize: 20, fontWeight: 700, color: color ? color.fg : "#1C2333" }}>{value}</Typography> : value}
      <Typography sx={{ ...typography.bodySmall, fontSize: 11, color: color ? color.fg : "#5B6075" }}>{sub}</Typography>
    </Box>
  );
};

const Tag = ({ children }) => (
  <Box component="span" sx={{ bgcolor: INFO.bg, color: INFO.fg, border: `1px solid ${INFO.border}`, borderRadius: "999px", px: 1.25, py: 0.25, fontSize: 11, fontWeight: 600 }}>
    {children}
  </Box>
);

// Count + status message under the audience picker. onMoveTo(date): "Move to <fit time>" (scheduled, over)
export const LimitAudienceNote = ({ view, showCount, onMoveTo, formatWhen }) => {
  if (!view) return null;
  const color = COLORS[view.tone];
  return (
    <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
      {showCount && (
        <Typography sx={{ ...typography.bodySmall, color: "#5B6075" }}>Phone number count: {fmt(view.audience)} (duplicates removed)</Typography>
      )}
      {view.title && (
        <Box role="alert" sx={{ bgcolor: color.bg, border: `1px solid ${color.border}`, color: color.fg, borderRadius: "8px", px: 1.75, py: 1.25, display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography sx={{ ...typography.bodySmall, fontWeight: 700, color: color.fg }}>{view.title}</Typography>
          <Typography sx={{ ...typography.bodySmall, color: color.fg }}>{view.body}</Typography>
          {view.fits_from && onMoveTo && (
            <Box
              component="button"
              type="button"
              onClick={() => onMoveTo(new Date(view.fits_from))}
              sx={{ alignSelf: "flex-start", mt: 0.5, border: `1px solid ${color.fg}`, bgcolor: "#fff", color: color.fg, borderRadius: "6px", px: 1.5, py: 1, minHeight: 36, fontFamily: "inherit", ...typography.bodySmall, fontWeight: 600, cursor: "pointer" }}
            >
              Move to {formatWhen ? formatWhen(new Date(view.fits_from)) : new Date(view.fits_from).toLocaleString()}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

const MessagingLimitCard = ({ view, isLater, whenText, loading, onRefresh }) => {
  if (!view) return null;
  const color = COLORS[view.tone];
  const remaining = view.remaining ?? 0;
  const adds = view.adds ?? 0;
  const usedPct = view.hasCap ? Math.min(100, (view.usedTotal / view.limit) * 100) : 0;
  const thisPct = view.hasCap ? Math.min(100 - usedPct, (adds / view.limit) * 100) : 0;
  const others = view.other_scheduled || [];

  return (
    <Box component="section" aria-labelledby="messaging-limit-title" sx={{ bgcolor: "#fff", border: "1px solid #E3E5EA", borderRadius: "12px", p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography id="messaging-limit-title" sx={{ ...typography.bodyLarge, fontWeight: 700 }}>
              {isLater && whenText ? `Daily messaging limit · at ${whenText}` : "Daily messaging limit"}
            </Typography>
            {isLater && <Tag>Projected</Tag>}
          </Box>
          <Typography sx={{ ...typography.bodySmall, color: "#5B6075" }}>
            {isLater
              ? `Projected for the 24 hours before the send time, using your current tier.${others.length ? ` Includes contacts from ${others.map((n) => `“${n}”`).join(", ")}.` : ""}`
              : "Unique contacts you can send template messages to in a rolling 24 hours."}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Typography sx={{ ...typography.bodySmall, color: "#5B6075" }}>
            {loading ? "Syncing…" : view.fetch_failed ? "Not synced" : `${view.stale ? "Last synced" : "Synced"} ${minutesAgo(view.synced_at)}`}
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={onRefresh}
            disabled={loading}
            sx={{ display: "flex", alignItems: "center", gap: 0.75, border: "1px solid #C9CCD8", bgcolor: "#fff", borderRadius: "6px", px: 1.5, py: 1, minHeight: 36, color: "#14204D", fontFamily: "inherit", ...typography.bodySmall, fontWeight: 500, cursor: loading ? "default" : "pointer" }}
          >
            {loading ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
            Refresh
          </Box>
        </Box>
      </Box>

      {(view.fetch_failed || view.stale) && (
        <Box role="status" sx={{ bgcolor: COLORS.warn.bg, border: `1px solid ${COLORS.warn.border}`, color: COLORS.warn.fg, borderRadius: "8px", px: 1.75, py: 1.25 }}>
          <Typography sx={{ ...typography.bodySmall, color: COLORS.warn.fg }}>
            {view.stale
              ? `Couldn't fetch the current tier from Gupshup. Using the last known tier (${view.unknown ? view.tier : tierShort(view)}) from ${minutesAgo(view.synced_at)}. Limits may have changed.`
              : "Couldn't fetch your current tier from Gupshup. Using 250 / day as a safe default until it's reachable."}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
        <Tile
          label="Current tier"
          value={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: 20, fontWeight: 700 }}>{view.unknown ? "Unknown" : tierShort(view)}</Typography>
              {view.tier && <Box component="span" sx={{ bgcolor: "#EEF0F6", color: "#14204D", borderRadius: "4px", px: 0.75, py: 0.25, fontSize: 11, fontWeight: 500 }}>{view.tier}</Box>}
            </Box>
          }
          sub={view.fetch_failed ? "Default · Gupshup unreachable" : view.stale ? "Last known · Gupshup unreachable" : "From Gupshup"}
        />
        <Tile label={isLater ? "Used in window" : "Used · last 24h"} value={fmt(view.usedTotal)} sub={isLater ? "Delivered + scheduled campaigns" : "via BotBridge"} />
        <Tile
          label={isLater ? "Projected left" : "Remaining now"}
          value={view.hasCap ? fmt(remaining) : "No cap"}
          sub={view.hasCap ? `of ${fmt(view.limit)} in 24h` : view.unknown ? "Not checked" : "Unlimited tier"}
        />
        <Tile
          label="This broadcast"
          tone={view.tone}
          value={fmt(view.audience)}
          sub={!view.hasCap ? "Within limit" : adds > remaining ? `${fmt(adds - remaining)} over the limit` : `${fmt(remaining - adds)} left after send`}
        />
      </Box>

      {view.hasCap && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Box
            role="img"
            aria-label={`${fmt(view.usedTotal)} used, ${fmt(adds)} in this broadcast, of ${fmt(view.limit)}`}
            sx={{ height: 10, bgcolor: "#EEF0F6", borderRadius: "999px", overflow: "hidden", display: "flex" }}
          >
            <Box sx={{ width: `${usedPct}%`, bgcolor: "#6B7090" }} />
            <Box sx={{ width: `${thisPct}%`, bgcolor: color.bar }} />
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {[["#6B7090", "Already used"], [color.bar, "This broadcast"], ["#EEF0F6", "Free"]].map(([swatch, label]) => (
              <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: swatch, border: label === "Free" ? "1px solid #C9CCD8" : "none" }} />
                <Typography sx={{ ...typography.bodySmall, fontSize: 11, color: "#5B6075" }}>{label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MessagingLimitCard;
